/* app.js — Seafresh Trading shared JS */

// ─── THEME: locked to dark mode ──────────────────────────
document.documentElement.setAttribute('data-theme', 'dark');

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ─── INTRO: DIVE FROM SKY INTO THE OCEAN (homepage) ──────
// The head script sets html.no-intro for repeat views / reduced motion.
// Fires 'intro:done' on window either way so the hero can (re)start.
(function () {
  const intro = document.getElementById('intro');
  if (!intro) return;

  // Deferred a tick so listeners registered later in this script
  // (the hero carousel) are attached before the event fires.
  const done = () => setTimeout(() => window.dispatchEvent(new CustomEvent('intro:done')), 0);

  if (document.documentElement.classList.contains('no-intro') || REDUCED_MOTION) {
    intro.remove();
    done();
    return;
  }

  try { sessionStorage.setItem('sf-intro-seen', '1'); } catch (e) {}
  document.body.classList.add('intro-lock');
  window.scrollTo(0, 0);

  let ended = false;
  function end() {
    if (ended) return;
    ended = true;
    intro.classList.add('intro-done');
    document.body.classList.remove('intro-lock');
    done();
    setTimeout(() => intro.remove(), 1000);
  }

  // Auto-finish after the full sequence; any interaction skips ahead
  const autoEnd = setTimeout(end, 7500);
  const skipNow = () => { clearTimeout(autoEnd); end(); };
  intro.addEventListener('click', skipNow);
  intro.querySelector('.intro-skip')?.addEventListener('click', skipNow);
  window.addEventListener('keydown', function onKey(e) {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      window.removeEventListener('keydown', onKey);
      skipNow();
    }
  });
  window.addEventListener('wheel', skipNow, { once: true, passive: true });
  window.addEventListener('touchmove', skipNow, { once: true, passive: true });
})();

// ─── SCROLL REVEAL (IntersectionObserver) ────────────────
// Pairs with `html.js .reveal` in style.css. Works in every browser and
// re-scans when pages inject new content (e.g. the store grid re-render).
(function () {
  if (!('IntersectionObserver' in window) || REDUCED_MOTION) {
    document.querySelectorAll('.reveal').forEach(el => el.classList.add('in-view'));
    window.__revealScan = () =>
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('in-view'));
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        io.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

  const scan = () => document.querySelectorAll('.reveal:not(.in-view)').forEach(el => io.observe(el));
  window.__revealScan = scan;
  scan();
})();

// ─── STAT COUNT-UP ───────────────────────────────────────
// Animates the leading number in stat blocks (25+, 12+, 2006, 100%) when
// they scroll into view. Non-numeric stats (IQF, B2B, CA) are left alone.
(function () {
  const els = document.querySelectorAll('.hero-stat strong, .about2-stat strong');
  if (!els.length || REDUCED_MOTION || !('IntersectionObserver' in window)) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      const node = [...entry.target.childNodes].find(n => n.nodeType === 3 && /\d/.test(n.textContent));
      if (!node) return;
      // Preserve any text around the number ("25+" keeps its "+")
      const m = node.textContent.match(/^(\D*)(\d+)(\D*)$/);
      if (!m) return;
      const [, prefix, digits, suffix] = m;
      const target = parseInt(digits, 10);
      const DURATION = 1400;
      const start = performance.now();
      const ease = t => 1 - Math.pow(1 - t, 3);
      (function tick(now) {
        const p = Math.min((now - start) / DURATION, 1);
        node.textContent = prefix + Math.round(target * ease(p)) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      })(start);
    });
  }, { threshold: 0.6 });

  els.forEach(el => io.observe(el));
})();

// ─── SCROLL PROGRESS BAR ─────────────────────────────────
(function () {
  const bar = document.createElement('div');
  bar.className = 'scroll-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  let ticking = false;
  const update = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`;
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  update();
})();

// ─── RISING BUBBLES (ocean ambience) ─────────────────────
(function () {
  const ocean = document.querySelector('.ocean-bg');
  if (!ocean || REDUCED_MOTION) return;
  const COUNT = 12;
  for (let i = 0; i < COUNT; i++) {
    const b = document.createElement('div');
    b.className = 'bubble';
    const size = 5 + Math.random() * 16;
    b.style.width = `${size}px`;
    b.style.height = `${size}px`;
    b.style.left = `${Math.random() * 100}%`;
    b.style.animationDuration = `${9 + Math.random() * 14}s`;
    b.style.animationDelay = `${Math.random() * 18}s`;
    ocean.appendChild(b);
  }
})();

// ─── STICKY NAV ──────────────────────────────────────────
(function () {
  const nav = document.querySelector('.site-nav');
  if (!nav) return;
  const onScroll = () => nav.classList.toggle('scrolled', window.scrollY > 40);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
})();

// ─── HAMBURGER ───────────────────────────────────────────
(function () {
  const btn = document.querySelector('.nav-hamburger');
  const menu = document.querySelector('.nav-mobile');
  if (!btn || !menu) return;
  const setOpen = (open) => {
    btn.classList.toggle('open', open);
    menu.classList.toggle('open', open);
    btn.setAttribute('aria-expanded', String(open));
    menu.setAttribute('aria-hidden', String(!open));
  };
  btn.addEventListener('click', () => setOpen(!menu.classList.contains('open')));
  // Close on link click
  menu.querySelectorAll('a').forEach(a => a.addEventListener('click', () => setOpen(false)));
  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('open')) setOpen(false);
  });
})();

// ─── ACTIVE NAV LINK ─────────────────────────────────────
(function () {
  const path = window.location.pathname;
  document.querySelectorAll('.nav-links a, .nav-mobile a').forEach(a => {
    const href = a.getAttribute('href');
    if (!href) return;
    if (
      (href === '/' && (path === '/' || path === '')) ||
      (href.includes('store') && path.includes('store')) ||
      (href.includes('about') && path.includes('about')) ||
      (href.includes('contact') && path.includes('contact'))
    ) {
      a.classList.add('active');
    }
  });
})();

// ─── HERO CAROUSEL ───────────────────────────────────────
(function () {
  const track     = document.querySelector('.carousel-track');
  const dotsWrap  = document.querySelector('.carousel-dots');
  const prev      = document.querySelector('.carousel-prev');
  const next      = document.querySelector('.carousel-next');
  if (!track) return;

  const slides = track.querySelectorAll('.carousel-slide');
  const count  = slides.length;
  let current  = 0;
  let timer;
  const DELAY  = 12000;

  // Fix track width to match slide count
  track.style.width = `${count * 100}%`;
  slides.forEach(s => s.style.width = `${100 / count}%`);

  // Dynamically build dots — only if more than 1 slide
  let dots = [];
  if (dotsWrap) {
    if (count <= 1) {
      dotsWrap.style.display = 'none';
      if (prev) prev.style.display = 'none';
      if (next) next.style.display = 'none';
    } else {
      for (let i = 0; i < count; i++) {
        const btn = document.createElement('button');
        btn.className = 'carousel-dot' + (i === 0 ? ' active' : '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Slide ${i + 1}`);
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(btn);
        dots.push(btn);
      }
    }
  }

  function goTo(idx) {
    slides[current].classList.remove('active');
    if (dots[current]) { dots[current].classList.remove('active'); dots[current].setAttribute('aria-selected','false'); }
    current = (idx + count) % count;
    slides[current].classList.add('active');
    if (dots[current]) { dots[current].classList.add('active'); dots[current].setAttribute('aria-selected','true'); }
    track.style.transform = `translateX(-${current * (100 / count)}%)`;
    resetTimer();
  }

  function resetTimer() {
    clearInterval(timer);
    timer = setInterval(() => goTo(current + 1), DELAY);
  }

  prev?.addEventListener('click', () => goTo(current - 1));
  next?.addEventListener('click', () => goTo(current + 1));

  // Touch swipe
  let startX = 0;
  track.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
  track.addEventListener('touchend',   e => {
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 50) goTo(dx < 0 ? current + 1 : current - 1);
  });

  goTo(0);

  // Replay the first slide's entrance once the intro overlay lifts,
  // so the hero choreography lands in front of the viewer (not behind
  // the intro). Fires immediately when the intro is skipped.
  window.addEventListener('intro:done', () => goTo(current));

  // ─ Arrow idle-hide: show on mouse/touch activity, hide after 3s idle ─
  const heroCarousel = document.querySelector('.hero-carousel');
  let idleTimer;
  function showArrows() {
    heroCarousel?.classList.add('hero-active');
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => heroCarousel?.classList.remove('hero-active'), 3000);
  }
  // Show on any mouse movement over the hero section
  heroCarousel?.addEventListener('mousemove', showArrows);
  // Also show briefly on touch (tap) so mobile users can see/use them
  heroCarousel?.addEventListener('touchstart', showArrows, { passive: true });
  // Show on arrow button focus (keyboard nav)
  prev?.addEventListener('focus', showArrows);
  next?.addEventListener('focus', showArrows);
})();

// ─── ABOUT SECTION CAROUSEL (products showcase on landing) ──
(function () {
  const carousels = document.querySelectorAll('.products-carousel');
  carousels.forEach(carousel => {
    const track = carousel.querySelector('.pc-track');
    const prev  = carousel.querySelector('.pc-prev');
    const next  = carousel.querySelector('.pc-next');
    if (!track) return;

    let isDragging = false;
    let dragMoved = false;
    let startX, scrollLeft;

    track.addEventListener('mousedown', e => { isDragging = true; dragMoved = false; startX = e.pageX - track.offsetLeft; scrollLeft = track.scrollLeft; });
    track.addEventListener('mouseleave', () => isDragging = false);
    track.addEventListener('mouseup', () => isDragging = false);
    track.addEventListener('mousemove', e => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      if (Math.abs(x - startX) > 4) dragMoved = true;
      track.scrollLeft = scrollLeft - (x - startX) * 1.5;
    });
    // A drag that ends over a card must not follow the card's link
    track.addEventListener('click', e => {
      if (dragMoved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    const SCROLL_AMT = 300;
    prev?.addEventListener('click', () => track.scrollBy({ left: -SCROLL_AMT, behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left:  SCROLL_AMT, behavior: 'smooth' }));
  });
})();

// ─── SCROLL TO TOP ───────────────────────────────────────
(function () {
  const btn = document.querySelector('.scroll-top');
  if (!btn) return;
  window.addEventListener('scroll', () => {
    btn.style.opacity = window.scrollY > 400 ? '1' : '0';
    btn.style.pointerEvents = window.scrollY > 400 ? 'auto' : 'none';
  }, { passive: true });
  btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
})();
